import { StudioIntegrationTemplate } from '@goldcast/api/backend';

import { getInitialState } from './index';
import { State, Templates, SortBy, Params, FilterBy, TemplateWebhook } from './types';

import { ScheduledEmail } from '@/studio/emailSchedule/store/types';
import { CrmObjectType, FieldMapping, FieldMappingRowError, SourceField } from '@/studio/orgIntegration/store/types';
import {
  getFieldMappingRowErrorDefaultState,
  getFieldMappingsDefaultState,
  getPrevFieldMappingsDefaultState,
  getSaveFieldMappingLoadingDefaultState
} from '@/studio/orgIntegration/store/utils';

const mutations = {
  setTemplates(state: State, templates: Templates) {
    state.templates = templates;
  },
  setIsLoading(state: State, isLoading: boolean) {
    state.isLoading = isLoading;
  },
  setSearchTerm(state: State, newSearchTerm: string) {
    state.searchTerm = newSearchTerm;
  },
  setCurrentPage(state: State, currentPage: number) {
    state.currentPage = currentPage;
  },
  setSortBy(state: State, sortBy: SortBy) {
    state.sortBy = sortBy;
  },
  setFilterBy(state: State, filterBy: FilterBy) {
    state.filterBy = filterBy;
  },
  setTemplatesCount(state: State, templatesCount: number) {
    state.templatesCount = templatesCount;
  },
  setTemplateListParamsState(state: State, params: Params) {
    state.sortBy = params.sortBy;
    state.searchTerm = params.searchTerm;
    state.currentPage = params.currentPage;
  },
  updateTemplate(state: State, template: Templates[number]) {
    state.templates[template.id] = template;
  },
  updateTemplates(state: State, templates: Templates) {
    state.templates = {
      ...state.templates,
      ...templates
    };
  },
  createTemplate(state: State, template: Templates) {
    state.templates = {
      ...template,
      ...state.templates
    };
  },
  setRegistrationEmail(state: State, { registrationEmail }: { registrationEmail: ScheduledEmail }) {
    state.registrationEmail = registrationEmail;
  },
  setEmailTemplateLoading(state: State, isLoading: boolean) {
    state.isEmailTemplateLoading = isLoading;
  },
  setCurrentTemplateId(state: State, templateId: string) {
    state.currentTemplateId = templateId;
  },
  removeTemplateFromStore(state: State, templateId: string) {
    delete state.templates[templateId];
  },
  resetTemplateListState(state: State) {
    Object.assign(state, getInitialState());
  },
  setCurrentIntegrationTemplate(state: State, integrationTemplate: StudioIntegrationTemplate) {
    state.currentIntegrationTemplate = integrationTemplate;
  },
  setLoadingIntegrationTemplate(state: State, isLoading: boolean) {
    state.isIntegrationTemplateLoading = isLoading;
  },
  updateIntegrationTemplate(state: State, integrationTemplate: StudioIntegrationTemplate) {
    state.currentIntegrationTemplate = integrationTemplate;
  },
  setIntegrationTemplateUpdating(state: State, isUpdating: boolean) {
    state.isIntegrationTemplateUpdating = isUpdating;
  },
  setTemplateNotificationsLoading(state: State, isLoading: boolean) {
    state.isTemplateNotificationsLoading = isLoading;
  },
  setTemplateNotifications(state: State, notifications: any[]) {
    state.templateNotifications = notifications;
  },
  setTemplateWebhooksLoading(state: State, isLoading: boolean) {
    state.isTemplateWebhooksLoading = isLoading;
  },
  setTemplateWebhooks(state: State, webhooks: TemplateWebhook[]) {
    state.templateWebhooks = webhooks;
  },
  // Template field mapping ************************************************
  setTemplateSourceFieldLoading(state: State, isTemplateSourceFieldLoading: boolean) {
    state.isTemplateSourceFieldLoading = isTemplateSourceFieldLoading;
  },
  setTemplateSourceFields(state: State, templateSourceFields: SourceField[]) {
    state.templateSourceFields = templateSourceFields;
  },
  initializeTemplateFieldMappingStore(state: State, crmObjectTypes: CrmObjectType[]) {
    state.templateFieldMappings = getFieldMappingsDefaultState(crmObjectTypes);
    state.prevTemplateFieldMappings = getPrevFieldMappingsDefaultState(crmObjectTypes);
    state.templateFieldMappingRowError = getFieldMappingRowErrorDefaultState(crmObjectTypes);
    state.isSaveTemplateFieldMappingLoading = getSaveFieldMappingLoadingDefaultState(crmObjectTypes);
  },
  setTemplateFieldMappingLoading(
    state: State,
    { crmObjectType, isFieldMappingLoading }: { crmObjectType: string; isFieldMappingLoading: boolean }
  ) {
    if (crmObjectType in state.templateFieldMappings) {
      state.templateFieldMappings[crmObjectType].isLoading = isFieldMappingLoading;
    }
  },
  setTemplateFieldMappings(
    state: State,
    { crmObjectType, templateFieldMappings }: { crmObjectType: string; templateFieldMappings: FieldMapping[] }
  ) {
    if (crmObjectType in state.templateFieldMappings) {
      state.templateFieldMappings[crmObjectType].mappings = templateFieldMappings;
    }
  },
  setPrevTemplateFieldMappings(
    state: State,
    { crmObjectType, prevTemplateFieldMappings }: { crmObjectType: string; prevTemplateFieldMappings: FieldMapping[] }
  ) {
    state.prevTemplateFieldMappings[crmObjectType] = prevTemplateFieldMappings;
  },
  setTemplateFieldMappingRowError(
    state: State,
    { crmObjectType, errorsArray }: { crmObjectType: string; errorsArray: (FieldMappingRowError | null)[] }
  ) {
    state.templateFieldMappingRowError[crmObjectType] = errorsArray;
  },
  addNewEmptyTemplateFieldMappingItem(
    state: State,
    { crmObjectType, emptyFieldMapping }: { crmObjectType: string; emptyFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.templateFieldMappings) {
      state.templateFieldMappings[crmObjectType].mappings = [
        ...state.templateFieldMappings[crmObjectType].mappings,
        emptyFieldMapping
      ];
    }
  },
  removeTemplateFieldMappingItem(
    state: State,
    { crmObjectType, fieldMappingID }: { crmObjectType: string; fieldMappingID: string }
  ) {
    if (crmObjectType in state.templateFieldMappings) {
      state.templateFieldMappings[crmObjectType].mappings = state.templateFieldMappings[crmObjectType].mappings.filter(
        fieldMapping => fieldMapping.id !== fieldMappingID
      );
    }
  },
  updateTemplateFieldMappingItem(
    state: State,
    { crmObjectType, updatedFieldMapping }: { crmObjectType: string; updatedFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.templateFieldMappings) {
      state.templateFieldMappings[crmObjectType].mappings = state.templateFieldMappings[crmObjectType].mappings.map(
        fieldMapping => {
          if (fieldMapping.id === updatedFieldMapping.id) {
            return {
              ...fieldMapping,
              ...updatedFieldMapping
            };
          }
          return fieldMapping;
        }
      );
    }
  },
  discardTemplateFieldMappingChanges(state: State, crmObjectType: string) {
    if (!state?.templateFieldMappings[crmObjectType]?.mappings || !state?.prevTemplateFieldMappings[crmObjectType]) {
      return;
    }
    state.templateFieldMappings[crmObjectType].mappings = state.prevTemplateFieldMappings[crmObjectType];
    const defaultErrorArray = new Array(state.templateFieldMappings[crmObjectType].mappings.length).fill(null);
    state.templateFieldMappingRowError[crmObjectType] = defaultErrorArray;
  },
  setSaveTemplateFieldMappingLoading(
    state: State,
    {
      crmObjectType,
      isSaveTemplateFieldMappingLoading
    }: { crmObjectType: string; isSaveTemplateFieldMappingLoading: boolean }
  ) {
    state.isSaveTemplateFieldMappingLoading[crmObjectType] = isSaveTemplateFieldMappingLoading;
  }
};

export default mutations;
